import type { ScheduleEntry } from "@edu/shared";
import { studentCountText } from "../../lib/plurals.ts";
import { GroupsIcon, PersonIcon } from "../shell/icons.tsx";

/**
 * Личное занятие или групповое: фигура, а у групповой рядом число участников.
 *
 * Значком, а не словом (решение владельца 30.08.2026, правило целиком в разделе
 * 8б `docs/cabinet-tables-design.md`): строка сетки несёт четыре факта сразу, и
 * «6 учеников» съедало бы место у имени, ради которого в неё и смотрят. Число
 * при этом остаётся на экране цифрой: прячется слово, а не количество.
 *
 * ⚠️ **Подпись стоит НА САМОМ ЗНАЧКЕ**, и это единственное исключение из правила
 * «значок пустой для читалок» (`features/shell/icons.tsx`, шапка). Правило
 * держится на том, что слово стоит рядом; здесь слова нет вовсе, и без подписи
 * незрячий читатель потерял бы разницу между личным занятием и групповым
 * целиком. Число в подписи считает `studentCountText`, а не пишется на глаз: «6
 * ученика» замечает ровно тот, кому мы это читаем.
 *
 * ⚠️ Та же подпись всплывает подсказкой под мышью, и источник у неё ОДИН
 * (`<title>` внутри значка, `features/shell/icons.tsx`). Зрячий человек, не
 * опознавший фигуру, иначе не может узнать её название ничем, а два источника
 * (подпись для читалки плюс своя подсказка) читались бы вслух дважды.
 *
 * ⚠️ Цифра рядом со значком для читалки скрыта: она уже прочитана подписью
 * («Группа, 6 учеников»), и вторым разом дала бы «Группа, 6 учеников 6».
 *
 * ⚠️ **Свой файл, а не часть календаря** (правка по итогам ревью 30.08.2026).
 * Фигуру показывают двое, коробка сетки и раскрытая карточка, и пока компонент
 * жил в `CalendarGrid.tsx`, карточка тянула за собой весь календарь ради одного
 * значка. Двух копий при этом быть не должно: разъехавшись, они дадут читалке
 * разную подпись в сетке и в карточке.
 *
 * ⚠️ **Компонентов ДВА, и граница между ними это ровно то, что чинил второй круг
 * ревью 30.08.2026.** Список серий рисовал фигуру группы со своей подписью, и
 * доводом была сигнатура: `EntryKind` принимает целое занятие, а у списка серий
 * занятия нет. Довод снят сужением: рисует `KindMark`, ему нужны только вид и
 * число, а роль смотрящего и вытаскивание поля остались обёрткой. Двух подписей
 * одной фигуры в кабинете больше нет.
 */

/**
 * Фигура вида занятия, а у группового число участников рядом.
 *
 * ⚠️ **Число у группы может отсутствовать, и это не «ноль».** В списке серий
 * есть строка пучка, у которого не осталось ни одной действующей серии:
 * расписание кончилось, и на вопрос «сколько человек ходит» верного числа нет
 * вовсе. Ноль был бы ответом не на тот вопрос («группа опустела»), поэтому там
 * стоит фигура без числа, а кончившееся расписание строка называет словами.
 */
export function KindMark(props: { kind: "student" } | { kind: "group"; size: number | null }) {
  if (props.kind === "student") {
    return (
      <span className="kind">
        <PersonIcon className="kind__ico" label="Личное занятие" />
      </span>
    );
  }
  const size = props.size;
  return (
    <span className="kind">
      <GroupsIcon
        className="kind__ico"
        label={size === null ? "Группа" : `Группа, ${studentCountText(size)}`}
      />
      {size !== null && <span aria-hidden="true">{size}</span>}
    </span>
  );
}

export function EntryKind({ entry }: { entry: ScheduleEntry }) {
  /**
   * ⚠️ **Вид приезжает ПОЛЕМ, а не угадывается по наличию `group`**, и до
   * 02.09.2026 угадывался. Поле `group` несёт только свёрнутая строка занятия
   * серии, а строка второго потока (осиротевший урок занятия группы: расписание
   * поправили после того, как оно прошло) не несёт его никогда, и такое занятие
   * получало здесь фигуру одного человека.
   *
   * ⚠️ Ученику не достаётся ни значка, ни числа: `kind` в его ответе нет НИ У
   * ОДНОГО занятия (`apps/api/src/schedule-grid.ts`), потому что раздел 3
   * `docs/group-lessons-design.md` запрещает ему отличать групповое занятие от
   * личного. Отдельной проверки роли здесь поэтому нет: отсутствие поля и есть
   * ответ «мы этого не сказали».
   */
  if (entry.kind === undefined) return null;
  if (entry.kind === "student") return <KindMark kind="student" />;
  /**
   * Числа у осиротевшего урока группы нет: `group` несёт только свёрнутая
   * строка, а сколько человек было на том занятии, эта строка не знает. Ноль и
   * единица тут были бы ответом не на тот вопрос, поэтому фигура без числа.
   */
  return <KindMark kind="group" size={entry.group?.size ?? null} />;
}
